import { useState, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import clsx from 'clsx'
import { ForecastResult } from '../../utils/api'
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard'
import { CategoryBadge } from './ForecastBadges'
import { ProbabilityDisplay } from './ProbabilityDisplay'

interface ForecastResultViewProps {
  result: ForecastResult
  forecastId?: string
  onReuseQuestion?: (question: string) => void
  onDelete?: () => void
}

interface CollapsibleSectionProps {
  title: string
  icon: string
  iconColor: string
  children: ReactNode
  defaultOpen?: boolean
  count?: number
}

function CollapsibleSection({
  title,
  icon,
  iconColor,
  children,
  defaultOpen = true,
  count,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border border-star-dim/10 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-void-50/50 hover:bg-void-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className={iconColor}>{icon}</span>
          <span className="text-sm font-mono text-star">{title}</span>
          {count !== undefined && (
            <span className="text-xs text-star-dim bg-star-dim/10 px-1.5 py-0.5 rounded">
              {count}
            </span>
          )}
        </div>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-star-dim"
        >
          ▼
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="p-3 border-t border-star-dim/10">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export function ForecastResultView({
  result,
  forecastId,
  onReuseQuestion,
  onDelete,
}: ForecastResultViewProps) {
  const { report } = result
  const [tab, setTab] = useState<'overview' | 'markdown' | 'details'>('overview')
  const { copy } = useCopyToClipboard()

  const copyLink = async () => {
    if (!forecastId) return
    const url = new URL(window.location.href)
    url.searchParams.set('id', forecastId)
    await copy(url.toString(), 'Link')
  }

  const downloadJson = () => {
    try {
      const payload = {
        forecast_id: forecastId,
        report: result.report,
        compiled: result.compiled,
        prediction: result.prediction,
      }
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `forecast-${forecastId || 'result'}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      // Ignore
    }
  }

  const TabButton = ({
    value,
    children,
  }: {
    value: 'overview' | 'markdown' | 'details'
    children: ReactNode
  }) => (
    <button
      type="button"
      onClick={() => setTab(value)}
      className={clsx(
        'px-3 py-1.5 rounded-lg text-xs font-mono border transition-all',
        tab === value
          ? 'bg-signal-blue/10 border-signal-blue/40 text-signal-blue'
          : 'bg-void-50 border-star-dim/20 text-star-muted hover:text-star hover:border-star-dim/40'
      )}
    >
      {children}
    </button>
  )

  return (
    <div className="space-y-6">
      {/* Header actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CategoryBadge category={report.category} />
          {forecastId && <span className="text-xs text-star-dim font-mono">#{forecastId}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => copy(result.report_markdown, 'Markdown')}
            className="px-3 py-1.5 rounded-lg text-xs font-mono bg-void-50 border border-star-dim/20 text-star-muted hover:text-star hover:border-signal-blue/40 transition-all"
          >
            Copy markdown
          </button>
          {forecastId && (
            <button
              type="button"
              onClick={copyLink}
              className="px-3 py-1.5 rounded-lg text-xs font-mono bg-void-50 border border-star-dim/20 text-star-muted hover:text-star hover:border-signal-blue/40 transition-all"
            >
              Copy link
            </button>
          )}
          <button
            type="button"
            onClick={() =>
              onReuseQuestion?.(result.compiled?.analysis?.raw_question || report.question)
            }
            className="px-3 py-1.5 rounded-lg text-xs font-mono bg-void-50 border border-star-dim/20 text-star-muted hover:text-star hover:border-star-dim/40 transition-all"
          >
            Reuse question
          </button>
          <button
            type="button"
            onClick={downloadJson}
            className="px-3 py-1.5 rounded-lg text-xs font-mono bg-void-50 border border-star-dim/20 text-star-muted hover:text-star hover:border-star-dim/40 transition-all"
          >
            Export JSON
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="px-3 py-1.5 rounded-lg text-xs font-mono bg-signal-red/10 border border-signal-red/30 text-signal-red hover:bg-signal-red/15 transition-all"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2">
        <TabButton value="overview">Overview</TabButton>
        <TabButton value="markdown">Full report</TabButton>
        <TabButton value="details">Details</TabButton>
      </div>

      {tab === 'overview' && (
        <div className="space-y-5">
          {/* Question */}
          <div className="text-center">
            <p className="text-lg text-star font-medium">{report.question}</p>
          </div>

          {/* Verdict with Gauge */}
          <div className="py-4">
            <ProbabilityDisplay probability={report.probability} confidence={report.confidence} large />
          </div>

          {/* Summary Card */}
          <div className="p-4 bg-gradient-to-br from-signal-blue/5 to-signal-purple/5 border border-signal-blue/20 rounded-lg">
            <p className="text-star leading-relaxed">{report.summary}</p>
          </div>

          {/* Collapsible Evidence Sections */}
          <div className="space-y-3">
            {/* Key Factors */}
            {report.key_factors.length > 0 && (
              <CollapsibleSection
                title="Key Factors"
                icon="🎯"
                iconColor="text-signal-blue"
                count={report.key_factors.length}
                defaultOpen={true}
              >
                <ul className="space-y-2">
                  {report.key_factors.map((factor, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-signal-blue/20 text-signal-blue flex items-center justify-center text-xs font-mono">
                        {i + 1}
                      </span>
                      <span className="text-star">{factor}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>
            )}

            {/* Supporting Evidence */}
            {report.supporting_evidence.length > 0 && (
              <CollapsibleSection
                title="Supporting Evidence"
                icon="✓"
                iconColor="text-signal-green"
                count={report.supporting_evidence.length}
                defaultOpen={false}
              >
                <ul className="space-y-2">
                  {report.supporting_evidence.map((evidence, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <span className="flex-shrink-0 text-signal-green mt-0.5">•</span>
                      <span className="text-star-muted">{evidence}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>
            )}

            {/* Risk Factors */}
            {report.risk_factors.length > 0 && (
              <CollapsibleSection
                title="Risk Factors"
                icon="⚠"
                iconColor="text-signal-amber"
                count={report.risk_factors.length}
                defaultOpen={false}
              >
                <ul className="space-y-2">
                  {report.risk_factors.map((risk, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <span className="flex-shrink-0 text-signal-amber mt-0.5">!</span>
                      <span className="text-star-muted">{risk}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>
            )}
          </div>

          {/* Metadata */}
          <div className="pt-4 border-t border-star-dim/10 flex items-center justify-between text-xs text-star-dim">
            <span>{new Date(report.timestamp).toLocaleString()}</span>
            <button
              type="button"
              onClick={() =>
                copy(
                  `${(report.probability * 100).toFixed(1)}% (conf ${(report.confidence * 100).toFixed(0)}%) — ${report.summary}`,
                  'Summary'
                )
              }
              className="text-signal-blue hover:text-signal-blue/80 font-mono"
            >
              Copy summary
            </button>
          </div>
        </div>
      )}

      {tab === 'markdown' && (
        <div className="space-y-3">
          <div className="text-xs text-star-dim">
            This is the generated markdown report (copy/paste friendly).
          </div>
          <pre className="p-4 bg-void-50 border border-star-dim/10 rounded-lg text-sm text-star whitespace-pre-wrap leading-relaxed">
            {result.report_markdown}
          </pre>
        </div>
      )}

      {tab === 'details' && (
        <div className="space-y-4">
          <div className="p-4 bg-void-50 rounded-lg space-y-3">
            <div>
              <div className="text-xs text-star-dim font-mono mb-1">Question</div>
              <div className="text-star">{report.question}</div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-star-dim font-mono mb-1">Compiled category</div>
                <div className="text-star">{result.compiled?.category || report.category}</div>
              </div>
              <div>
                <div className="text-xs text-star-dim font-mono mb-1">Target date</div>
                <div className="text-star">{result.compiled?.analysis?.target_date || '—'}</div>
              </div>
            </div>
            {result.compiled?.topics?.length > 0 && (
              <div>
                <div className="text-xs text-star-dim font-mono mb-1">Topics</div>
                <div className="flex flex-wrap gap-1">
                  {result.compiled.topics.map((t, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 bg-signal-blue/10 text-signal-blue text-xs rounded"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <details className="p-4 bg-void-50 border border-star-dim/10 rounded-lg">
            <summary className="cursor-pointer text-sm font-mono text-star-muted">
              Raw prediction payload
            </summary>
            <pre className="mt-3 text-xs text-star-dim overflow-x-auto max-h-[420px]">
              {JSON.stringify(result.prediction, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  )
}
